import _typeof from "@babel/runtime/helpers/typeof";
import _createForOfIteratorHelper from "@babel/runtime/helpers/createForOfIteratorHelper";
import { parseJsValue } from "./parseJsValue.js";
import { isExpressionString, isGeneralCallExpression, isNilPath, validateGlobalApi } from "./validations.js";
import { CALL_API_LIST } from "./constants.js";
var EXPECTED_ARGS = new Map([["callApi", [2, 3]], ["callHttp", [1, 2]], ["callTool", [2, 3]]]);
export function parseCallApi(path, state, app, options) {
  if (!isGeneralCallExpression(path)) {
    state.errors.push({
      message: "Expect a call expression here, received ".concat(path.node.type),
      node: path.node,
      severity: "error"
    });
    return null;
  }
  var callee = path.get("callee");
  var calleeName;
  if (callee.isIdentifier()) {
    var _iterator = _createForOfIteratorHelper(CALL_API_LIST),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var name = _step.value;
        if (validateGlobalApi(callee, name)) {
          calleeName = name;
          break;
        }
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    if (!calleeName) {
      state.errors.push({
        message: "Unknown API: \"".concat(callee.node.name, "\""),
        node: callee.node,
        severity: "error"
      });
      return null;
    }
  } else {
    state.errors.push({
      message: "Expect an identifier here, received ".concat(callee.type),
      node: callee.node,
      severity: "error"
    });
    return null;
  }
  var expectedArgs = EXPECTED_ARGS.get(calleeName);
  var args = path.get("arguments");
  if (!expectedArgs) {
    if (calleeName === "callProvider") {
      if (args.length < 1) {
        state.errors.push({
          message: "\"".concat(calleeName, "()\" expects at least 1 argument, but got ").concat(args.length),
          node: path.node,
          severity: "error"
        });
        return null;
      }
      var _firstArg = args[0];
      if (!_firstArg.isStringLiteral()) {
        state.errors.push({
          message: "\"".concat(calleeName, "()\" expects a string literal as the first argument, but got ").concat(_firstArg.type),
          node: _firstArg.node,
          severity: "error"
        });
        return null;
      }
      return {
        api: _firstArg.node.value,
        isRawProvider: true,
        params: args.slice(1).map(function (arg) {
          return parseJsValue(arg, state, app, options);
        })
      };
    }

    // copyText / showDialog
    return {
      api: calleeName === "copyText" ? "basic.copy-to-clipboard" : "basic.show-dialog",
      isRawProvider: true,
      params: args.map(function (arg) {
        return parseJsValue(arg, state, app, options);
      })
    };
  }
  var missingArgs = args.length < expectedArgs[0];
  if (missingArgs || !expectedArgs.includes(args.length)) {
    state.errors.push({
      message: "\"".concat(calleeName, "()\" expects ").concat(expectedArgs.join(" or "), " arguments, but got ").concat(args.length),
      node: path.node,
      severity: missingArgs ? "error" : "warning"
    });
    if (missingArgs) {
      return null;
    }
  }
  var payload;
  var firstArg = args[0];
  if (calleeName === "callHttp") {
    var value = parseJsValue(firstArg, state, app, options);
    if (typeof value !== "string") {
      state.errors.push({
        message: "\"".concat(calleeName, "()\" expects a string value as the first argument, but got ").concat(_typeof(value)),
        node: firstArg.node,
        severity: "error"
      });
      return null;
    }
    payload = {
      api: value,
      http: true
    };
  } else if (calleeName === "callApi") {
    if (!firstArg.isStringLiteral()) {
      state.errors.push({
        message: "\"".concat(calleeName, "()\" expects a string literal as the first argument, but got ").concat(firstArg.type),
        node: firstArg.node,
        severity: "error"
      });
      return null;
    }
    payload = {
      api: firstArg.node.value
    };
  } else {
    var conversationId = parseJsValue(firstArg, state, app, options);
    if (typeof conversationId !== "string") {
      state.errors.push({
        message: "\"".concat(calleeName, "()\" expects a string value as the first argument, but got ").concat(_typeof(conversationId)),
        node: firstArg.node,
        severity: "error"
      });
      return null;
    }
    var secondArg = args[1];
    var stepId = parseJsValue(secondArg, state, app, options);
    if (typeof stepId !== "string") {
      state.errors.push({
        message: "\"".concat(calleeName, "()\" expects a string value as the second argument, but got ").concat(_typeof(stepId)),
        node: secondArg.node,
        severity: "error"
      });
      return null;
    }
    var paramsNode = args[2];
    var params;
    if (paramsNode) {
      params = parseJsValue(paramsNode, state, app, options);
    }
    payload = {
      api: calleeName,
      tool: {
        conversationId: conversationId,
        stepId: stepId
      },
      params: params
    };
  }
  if (calleeName === "callApi" || calleeName === "callHttp") {
    var valuePath = args[1];
    if (valuePath && !isNilPath(valuePath)) {
      if (!valuePath.isObjectExpression()) {
        state.errors.push({
          message: "Data source \"params\" prefers an object literal, but got ".concat(valuePath.type),
          node: valuePath.node,
          severity: "notice"
        });
      }
      var _params = parseJsValue(valuePath, state, app, options);
      if (isExpressionString(_params) || _typeof(_params) === "object" && _params !== null) {
        payload.params = _params;
      } else {
        state.errors.push({
          message: "API params expects an object or expression, but got ".concat(_typeof(_params)),
          node: valuePath.node,
          severity: "error"
        });
      }
      // TODO: set ambiguousProps when reward is enabled
    }
  }
  if (calleeName === "callApi") {
    var metaPath = args[2];
    if (metaPath && !isNilPath(metaPath)) {
      if (!metaPath.isObjectExpression()) {
        state.errors.push({
          message: "\"callApi()\" third param \"meta\" expects an object literal, but got ".concat(metaPath.type),
          node: metaPath.node,
          severity: "error"
        });
      } else {
        var props = metaPath.get("properties");
        var _iterator2 = _createForOfIteratorHelper(props),
          _step2;
        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var prop = _step2.value;
            if (!prop.isObjectProperty()) {
              state.errors.push({
                message: "\"callApi()\" third param \"meta\" expects object properties, but got ".concat(prop.type),
                node: prop.node,
                severity: "error"
              });
              continue;
            }
            var key = prop.get("key");
            if (!key.isIdentifier()) {
              state.errors.push({
                message: "\"callApi()\" third param \"meta\" property key must be an identifier, but got ".concat(key.type),
                node: key.node,
                severity: "error"
              });
              continue;
            }
            if (key.node.name !== "objectId") {
              state.errors.push({
                message: "\"callApi()\" third param \"meta\" property key must be \"objectId\", but got \"".concat(key.node.name, "\""),
                node: key.node,
                severity: "error"
              });
              continue;
            }
            var _value = prop.get("value");
            if (!isNilPath(_value)) {
              if (!_value.isStringLiteral()) {
                state.errors.push({
                  message: "\"callApi()\" third param \"meta\" property \"".concat(key.node.name, "\" expects a string literal, but got ").concat(_value.type),
                  node: _value.node,
                  severity: "error"
                });
                continue;
              }
              payload.objectId = _value.node.value;
            }
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
      }
    }
  }
  return payload;
}
//# sourceMappingURL=parseCallApi.js.map